import React, { useEffect, useState, useMemo, useRef } from "react";
import { ModalWithIcon } from "../../RfModal/ModalWithIcon";
import { renderPlanColumn } from "../../UpgradeModal/Credits/PlansComparisonCredits";
import {
    getPlanData,
    frequencies,
    getCorePlanTiers,
    getDefaultCoreTierIndex
} from "../../../../../clientServices/workspaceFeaturesCredits";
import { useProjectSelector } from "../../../hooks/useProjectSelector";
import { getTitleForWorkspacePlan } from "../../../../routes/workspace/utils";
import { CreditTierSelector } from "../../UpgradeModal/Credits/CreditTierSelector";
import { DowngradePlanTargets } from "../../../../../state/types";

export type BasicDowngradePlan = "basic" | "basicAnnual";

type CancelPlanBasicDowngradeModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onCancel: () => void;
    onAcceptBasicPlanDowngrade: (
        basicDowngradeFrequency: (typeof frequencies)[number],
        coreTierCredits?: number
    ) => void;
    currentPlan: any;
    targetPlan?: "basic" | "core";
    workspaceId: string;
    selectedDowngradePlan: DowngradePlanTargets;
    isDowngrade: boolean;
};

export function CancelPlanBasicDowngradeModal({
    isOpen,
    onClose,
    onCancel,
    onAcceptBasicPlanDowngrade,
    currentPlan,
    targetPlan = "core",
    workspaceId,
    selectedDowngradePlan,
    isDowngrade
}: CancelPlanBasicDowngradeModalProps) {
    const [frequency, setFrequency] = useState(frequencies[1]);
    const [loadingAction, setLoadingAction] = useState<string | null>(null);
    const projectSelector = useProjectSelector();

    const corePlanTiers = useMemo(() => getCorePlanTiers(), []);
    const [selectedCoreTierIndex, setSelectedCoreTierIndex] = useState(() =>
        getDefaultCoreTierIndex(corePlanTiers)
    );
    const selectedCoreTier = corePlanTiers[selectedCoreTierIndex] || corePlanTiers[0];

    const isCorePlan = targetPlan === "core";
    const isAnnual = frequency.interval === "annually";
    const coreTierOption = isCorePlan ? { selectedCoreTier } : {};

    const lastIsOpenRef = useRef(isOpen);
    useEffect(() => {
        if (isOpen === lastIsOpenRef.current) return;
        lastIsOpenRef.current = isOpen;

        eventTracker.event({
            type: `Churn Downgrade Offer Modal ${isOpen ? "Opened" : "Closed"}`,
            data: { workspaceId, currentPlan, selectedDowngradePlan, isDowngrade }
        });
    }, [isOpen]);

    const planTitle = getTitleForWorkspacePlan(targetPlan);
    const planData = getPlanData(
        currentPlan,
        frequency as any,
        [targetPlan] as any,
        null,
        projectSelector,
        coreTierOption as any
    );
    const targetPlanData = planData[targetPlan];

    const corePriceOverride = isCorePlan
        ? {
              price: isAnnual ? selectedCoreTier.annualPrice : selectedCoreTier.monthlyPrice,
              monthlyPrice: selectedCoreTier.monthlyPrice
          }
        : null;

    const tierSelectorSlot = isCorePlan ? (
        <CreditTierSelector
            tiers={corePlanTiers}
            selectedTier={selectedCoreTier}
            onSelect={(tier) => {
                const idx = corePlanTiers.indexOf(tier);
                setSelectedCoreTierIndex(idx >= 0 ? idx : 0);
            }}
            isAnnual={isAnnual}
            baseTier={corePlanTiers[0]}
        />
    ) : null;

    const handleAcceptBasicPlanDowngrade = async () => {
        eventTracker.event({
            type: "Churn Downgrade Offer Modal Accepted",
            data: { workspaceId, currentPlan, selectedDowngradePlan, isDowngrade }
        });

        setLoadingAction("downgrade");
        await onAcceptBasicPlanDowngrade(
            frequency,
            isCorePlan ? selectedCoreTier.credits : undefined
        );
        setLoadingAction(null);
    };

    const handleCancel = async () => {
        eventTracker.event({
            type: "Churn Downgrade Offer Modal Rejected",
            data: { workspaceId, currentPlan, selectedDowngradePlan, isDowngrade }
        });

        setLoadingAction("cancel");
        onCancel();
    };

    if (!targetPlanData) {
        return null;
    }

    return (
        <ModalWithIcon
            isOpen={isOpen}
            onClose={onClose}
            type="warning"
            icon="far fa-arrow-up-arrow-down"
            title={`Instead of canceling, would you like to switch to the ${planTitle}?`}
            cancelButtonText="No Thanks, Cancel Now"
            submitButtonText={`Downgrade to the ${planTitle}`}
            onCancel={handleCancel}
            onSubmit={handleAcceptBasicPlanDowngrade}
            submitButtonProps={{
                disabled: !!loadingAction,
                loading: loadingAction === "downgrade",
                type: "upgrade"
            }}
            cancelButtonProps={{ disabled: !!loadingAction, loading: loadingAction === "cancel" }}
            closeOnCancel={false}
        >
            <div className="mt-1">
                {renderPlanColumn({
                    planKey: targetPlan,
                    planDetails: targetPlanData,
                    getPlanButton: () => null,
                    frequency,
                    setFrequency,
                    recommended: true,
                    tierSelectorSlot,
                    priceOverride: corePriceOverride
                })}
            </div>
        </ModalWithIcon>
    );
}
